/**
 * Focus layers — manages open overlays (modals, drawers, popovers).
 * See `.agents/docs/focus-layers.md` for details.
 */
window.FocusLayer = (function () {
  /** Candidates for sequential navigation; `isTabbable` filters them. */
  const TABBABLE_CANDIDATES = [
    'a[href]',
    'area[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]',
  ].join(',');

  /** Disclosures that own Escape themselves. */
  const OWNS_ESCAPE = 'details[open], dropdown-selects[open], dropdown-selects-options[open]';

  /** @type {Layer[]} bottom → top. The last entry owns Escape and containment. */
  const stack = [];

  /** Elements this module set `inert` on, so it only ever clears its own. */
  const inerted = new Set();

  /** Shift state from the last Tab, so the focusin backstop wraps the way the user asked. */
  let shiftHeld = false;

  /** Set when a keydown Escape was consumed, so a disclosure's keyup cannot peel a second layer. */
  let swallowEscapeKeyup = false;

  /** A nested disclosure Escape was handed to, checked afterwards in case nobody took it. */
  let deferredDisclosure = null;

  /** Suppresses the focusin backstop while a pop is moving focus itself. */
  let settling = false;

  /**
   * @typedef {Object} Layer
   * @property {HTMLElement} el
   * @property {boolean} contain
   * @property {boolean} modal
   * @property {boolean} lockScroll
   * @property {HTMLElement|null} trigger - what to focus again on pop
   * @property {Function|null} onDismiss - what Escape means; null leaves Escape alone
   */

  function isRendered(el) {
    if (!el || !el.isConnected) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true });
    }
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  /**
   * A named radio group exposes only ONE member to sequential navigation: the
   * checked one, or the first if none is checked.
   */
  function isRedundantRadio(el, root) {
    if (el.type !== 'radio' || !el.name) return false;
    const scope = el.form || root;
    const group = scope.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`);
    if (group.length < 2) return false;
    const checked = Array.from(group).find((radio) => radio.checked);
    return checked ? el !== checked : el !== group[0];
  }

  function isTabbable(el, root) {
    if (el.disabled) return false;
    if (el.tabIndex < 0) return false;
    if (el.type === 'hidden') return false;
    if (el.closest('[inert]')) return false;
    if (el.matches('details:not([open]) > *:not(summary) *')) return false;
    if (isRedundantRadio(el, root)) return false;
    return isRendered(el);
  }

  /** Live list, in tab order. Not cached — a layer's contents change while it is open. */
  function tabbables(root) {
    if (!root) return [];
    const found = Array.from(root.querySelectorAll(TABBABLE_CANDIDATES)).filter((el) => isTabbable(el, root));
    // A positive tabindex jumps the queue.
    const positive = found.filter((el) => el.tabIndex > 0).sort((a, b) => a.tabIndex - b.tabIndex);
    return positive.length ? [...positive, ...found.filter((el) => el.tabIndex === 0)] : found;
  }

  function top() {
    return stack.length ? stack[stack.length - 1] : null;
  }

  /**
   * The topmost *containing* layer, acting as the boundary Tab may not cross.
   * Containment is dropped if a layer sits outside the boundary to keep it usable.
   */
  function containingTop() {
    let index = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].contain) {
        index = i;
        break;
      }
    }
    if (index < 0) return null;

    const layer = stack[index];
    for (const above of stack.slice(index + 1)) {
      if (!layer.el.contains(above.el)) return null;
    }
    // Re-checked per keystroke: a surface can hide itself without popping.
    return isRendered(layer.el) ? layer : null;
  }

  /** True when `node` sits inside a layer stacked above `el`'s layer. */
  function isAboveLayerTarget(node, el) {
    const index = stack.findIndex((layer) => layer.el === el);
    if (index < 0) return false;
    return stack.slice(index + 1).some((layer) => layer.el.contains(node));
  }

  /**
   * Determines the topmost modal layer (owner) and any layers stacked above it (outside)
   * that must stay reachable (e.g. non-modal notifications).
   */
  function isolation() {
    let index = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].modal) {
        index = i;
        break;
      }
    }
    if (index < 0) return null;

    const owner = stack[index];
    const outside = stack
      .slice(index + 1)
      .map((layer) => layer.el)
      .filter((el) => !owner.el.contains(el));

    return { owner, outside };
  }

  /** Inerts branches outside the active modal's ancestor path to prevent access. */
  function applyInert() {
    inerted.forEach((el) => {
      el.inert = false;
    });
    inerted.clear();

    const isolated = isolation();
    if (!isolated) return;

    let node = isolated.owner.el;
    while (node.parentElement) {
      const parent = node.parentElement;
      for (const sibling of parent.children) {
        if (sibling === node) continue;
        inertBranch(sibling, isolated.outside);
      }
      if (parent === document.body) break;
      node = parent;
    }
  }

  /**
   * Inerts a branch unless it holds a layer stacked above the owner, in which case
   * it recursively inerts off-path children to keep the top layer usable.
   */
  function inertBranch(el, outside) {
    if (el.inert) return; // author-set: not ours to clear later
    // Nothing inside a layer stacked above the owner is ever sealed.
    if (outside.some((target) => el === target || target.contains(el))) return;

    if (!outside.some((target) => el.contains(target))) {
      el.inert = true;
      inerted.add(el);
      return;
    }

    for (const child of el.children) inertBranch(child, outside);
  }

  /** Derived from the stack, never counted, so it cannot be stranded. */
  function applyScrollLock() {
    const locked = stack.some((layer) => layer.lockScroll);
    if (locked === document.body.classList.contains('no-scroll')) return;

    if (locked) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
      document.body.style.paddingRight = '';
    }
  }

  /** Applies `aria-modal="true"` only to the layer holding the isolation. */
  function applyAria() {
    const isolated = isolation();
    const claiming = isolated && !isolated.outside.length ? isolated.owner : null;

    stack.forEach((layer) => {
      if (!layer.modal) return;
      layer.el.setAttribute('aria-modal', layer === claiming ? 'true' : 'false');
    });
    const layer = top();
    if (layer && !layer.modal) layer.el.removeAttribute('aria-modal');
  }

  function setTriggerExpanded(layer, expanded) {
    const trigger = layer.trigger;
    if (!trigger || !trigger.hasAttribute('aria-expanded')) return;
    trigger.setAttribute('aria-expanded', String(expanded));
  }

  /** Moves focus into the layer and verifies it landed, trying each candidate. */
  function focusInto(layer, elementToFocus) {
    const candidates = elementToFocus ? [elementToFocus, ...tabbables(layer.el)] : tabbables(layer.el);

    for (const candidate of candidates) {
      if (!candidate || !layer.el.contains(candidate)) continue;

      candidate.focus({ preventScroll: true });
      if (document.activeElement !== candidate) continue;

      if (
        candidate.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(candidate.type) &&
        candidate.value
      ) {
        candidate.setSelectionRange(0, candidate.value.length);
      }
      return true;
    }

    if (!layer.el.hasAttribute('tabindex')) layer.el.setAttribute('tabindex', '-1');
    layer.el.focus({ preventScroll: true });
    return layer.el.contains(document.activeElement);
  }

  /** Removes unrendered layers and recomputes derived state (inert, lock, aria). */
  function reap() {
    let removed = false;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      const el = stack[i].el;
      if (!el.isConnected || !isRendered(el)) {
        stack.splice(i, 1);
        removed = true;
      }
    }
    if (removed) {
      applyInert();
      applyScrollLock();
      applyAria();
    }
  }

  /**
   * @param {HTMLElement} el - the layer root
   * @param {Object} [options]
   * @param {boolean} [options.contain=true] - keep Tab inside
   * @param {boolean} [options.modal=false] - inert the rest of the document
   * @param {boolean} [options.lockScroll] - defaults to `modal`
   * @param {HTMLElement} [options.initialFocus] - defaults to the first tabbable
   * @param {HTMLElement} [options.trigger] - defaults to the current activeElement
   * @param {Function} [options.onDismiss] - what Escape does; without it Escape is left alone
   */
  function push(el, options = {}) {
    if (!el) return null;
    reap();

    // Re-pushing an element that is already open (nested menu levels) moves it to
    // the top instead of stacking a duplicate.
    const existing = stack.findIndex((layer) => layer.el === el);
    if (existing >= 0) stack.splice(existing, 1);

    const layer = {
      el,
      contain: options.contain !== false,
      modal: options.modal === true,
      lockScroll: options.lockScroll ?? options.modal === true,
      trigger: options.trigger || (document.activeElement !== document.body ? document.activeElement : null),
      onDismiss: options.onDismiss || null,
    };
    stack.push(layer);

    applyInert();
    applyScrollLock();
    applyAria();
    setTriggerExpanded(layer, true);

    // The caller has just made the layer visible. Flush style and layout here so it
    // really is visible before focus is attempted — an element inside
    // `visibility: hidden` cannot take focus.
    el.getBoundingClientRect();

    const landed = focusInto(layer, options.initialFocus);
    if (!landed) {
      // The layer is not rendered yet — a transition that delays visibility. One retry.
      requestAnimationFrame(() => {
        if (stack.includes(layer)) focusInto(layer, options.initialFocus);
      });
    }

    return layer;
  }

  /**
   * @param {HTMLElement} [el] - the layer to remove; defaults to the top one
   * @param {Object} [options]
   * @param {HTMLElement} [options.returnFocus] - overrides the recorded trigger
   */
  function pop(el, options = {}) {
    reap();
    const index = el ? stack.findIndex((layer) => layer.el === el) : stack.length - 1;
    if (index < 0) return;

    const [layer] = stack.splice(index, 1);
    setTriggerExpanded(layer, false);
    if (layer.modal) layer.el.removeAttribute('aria-modal');

    // Un-inert before focusing: the element focus goes back to may sit inside a
    // layer that was inert while this one was on top.
    applyInert();
    applyScrollLock();
    applyAria();

    // Only take focus back if this layer still holds it, so a layer closing in the
    // background cannot steal it.
    const held = layer.el.contains(document.activeElement) || document.activeElement === document.body;
    if (!held) return;

    settling = true;
    try {
      const candidates = [options.returnFocus, layer.trigger];
      for (const candidate of candidates) {
        if (candidate && candidate.isConnected && !candidate.closest('[inert]') && isRendered(candidate)) {
          candidate.focus({ preventScroll: true });
          return;
        }
      }
      // The opener is gone. A layer still open underneath takes focus before the page
      // does: it is what the customer can see, and the page behind it is inert.
      const beneath = top();
      if (beneath && focusInto(beneath)) return;

      // Land on the main landmark rather than <body>, which would leave a keyboard
      // user tabbing from the top of the page. Skipped when it is inert — `focus()`
      // there is a silent no-op that would strand focus on <body> after all.
      const fallback = document.getElementById('MainContent') || document.querySelector('main');
      if (fallback && !fallback.closest('[inert]')) {
        if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
      }
    } finally {
      settling = false;
    }
  }

  function onKeyDown(event) {
    if (event.key === 'Tab') {
      shiftHeld = event.shiftKey;
      handleTab(event);
      return;
    }

    if (event.key !== 'Escape' || event.defaultPrevented) return;

    reap();
    const layer = top();
    if (!layer) return;

    // A disclosure nested inside the top layer collapses first; the layer goes on
    // the next press. Nested is the test — a layer built on <details> IS the open
    // disclosure, so it has nothing to defer to.
    const disclosure = event.target.closest?.(OWNS_ESCAPE);
    if (disclosure && disclosure !== layer.el && layer.el.contains(disclosure)) {
      // Not every disclosure has an owner, so check afterwards that something closed it.
      deferredDisclosure = disclosure;
      return;
    }

    // A layer with no dismiss handler is not ours to close.
    if (!layer.onDismiss) return;

    event.preventDefault();
    event.stopPropagation();
    swallowEscapeKeyup = true;
    layer.onDismiss();
  }

  /** Wraps Tab at the layer's live boundaries. */
  function handleTab(event) {
    const layer = containingTop();
    if (!layer) return;

    const items = tabbables(layer.el);
    const active = document.activeElement;

    if (!items.length) {
      event.preventDefault();
      return;
    }

    if (!layer.el.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? items[items.length - 1] : items[0]).focus({ preventScroll: true });
      return;
    }

    const index = items.indexOf(active);
    // Inside the layer but not on a known tabbable (an unchecked radio reached with
    // arrow keys). Let the browser move focus; the focusin backstop catches overshoot.
    if (index < 0) return;

    if (!event.shiftKey && index === items.length - 1) {
      event.preventDefault();
      items[0].focus({ preventScroll: true });
    } else if (event.shiftKey && index === 0) {
      event.preventDefault();
      items[items.length - 1].focus({ preventScroll: true });
    }
  }

  /**
   * Collapse a disclosure nobody claimed, so Escape is never a no-op. Runs a tick
   * after the keyup so its owner, if it has one, goes first.
   */
  function collapseUnclaimedDisclosure() {
    const disclosure = deferredDisclosure;
    deferredDisclosure = null;
    if (!disclosure?.isConnected || !disclosure.hasAttribute('open')) return;
    // Only <details> has a generic collapse. A custom element that ignored the key
    // is a bug in that element.
    if (disclosure.tagName !== 'DETAILS') return;

    disclosure.removeAttribute('open');
    const summary = disclosure.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.focus({ preventScroll: true });
  }

  function onKeyUp(event) {
    if (event.key !== 'Escape') return;

    if (deferredDisclosure) setTimeout(collapseUnclaimedDisclosure);

    if (!swallowEscapeKeyup) return;
    swallowEscapeKeyup = false;
    // Disclosure handlers listen on keyup; without this an ancestor of the layer
    // just closed would collapse on the same press. One press, one layer.
    event.stopPropagation();
  }

  function onFocusIn(event) {
    if (settling) return;
    const layer = containingTop();
    if (!layer || layer.el.contains(event.target)) return;

    const items = tabbables(layer.el);
    const target = shiftHeld ? items[items.length - 1] : items[0];
    (target || layer.el).focus({ preventScroll: true });
  }

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('keyup', onKeyUp, true);
  document.addEventListener('focusin', onFocusIn, true);

  return {
    push,
    pop,
    top,
    tabbables,
    isAboveLayerTarget,
    /** Depth and class names only — for debugging and automated checks. */
    inspect: () =>
      stack.map((layer) => ({
        el: layer.el.tagName.toLowerCase() + (layer.el.className ? `.${layer.el.className.split(' ')[0]}` : ''),
        contain: layer.contain,
        modal: layer.modal,
        dismissable: Boolean(layer.onDismiss),
      })),
  };
})();
